import React, { useEffect, useState } from "react";
import moment from "moment";

import CustomDropDown from "input/CustomDropDown";
import { navigate } from "utility/NavigationService";
import { renderLoader } from "utility/LoaderUtils";
import { getVisitBadgeImagePath } from "planEditor/BadgeUtils";

const SessionsPanel = ({
    coachingPlanDataFound,
    dataMissing,
    isLoading,
    patientId,
    rawSessionData
}) => {
    const [isConfigured, setIsConfigured] = useState<boolean>(false);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    const [weekOptions, setWeekOptions] = useState<{}[]>(null);

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            if (rawSessionData) {
                let options = [];

                Object.keys(rawSessionData).forEach(
                    (e) => {
                        options.push({
                            label: "Week " + e.toString(),
                            value: e
                        });
                    }
                );

                setSessionData(rawSessionData);
                const sortedOptions = options.sort((a, b) =>
                    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" })
                );
                setWeekOptions(sortedOptions);
                setSelectedWeek(sortedOptions.at(-1).value);
                setIsConfigured(true);
            }
        }, [rawSessionData]
    );

    // ---=== Render ===--- //

    const renderTable = () => {
        let table = [];

        Object.keys(sessionData[selectedWeek]).forEach(
            (dateKey: string, index: number) => {
                const sessionDate = moment(dateKey, "YYYY-MM-DD");
                const motionTime = sessionData[selectedWeek][dateKey].motion_time;
                const motionTimeDuration = moment.duration(motionTime * 1000);
                const activityCompletions = sessionData[selectedWeek][dateKey].activity_completions;

                table.push(
                    <React.Fragment key={`session-panel-row-${index.toString()}`}>
                        <tr
                            className="border-y-2 border-x-2 even:bg-white odd:bg-white border-gray-200 hover:bg-gray-100 cursor-pointer"
                            onClick={() => navigate(`/patient/${patientId}/day/${sessionDate.format("YYYY-MM-DD")}`)}
                        >
                            <td className="border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">{sessionDate.format("MMMM Do YYYY")}</td>
                            <td className="border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">{activityCompletions}</td>
                            <td className="border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                                {
                                    motionTime > 60
                                        ? `${Math.floor(motionTimeDuration.asHours()) > 0 ? `${Math.floor(motionTimeDuration.asHours())}h ` : ""}${motionTimeDuration.minutes() > 0 ? `${motionTimeDuration.minutes()}m` : ""}`
                                        : `${motionTimeDuration.seconds()}s`
                                }
                            </td>
                            <td className="border-x-0  px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px] ">
                                <img className="w-[30px] h-[37px]" src={getVisitBadgeImagePath(sessionData[selectedWeek][dateKey].session_badge)} alt="badge"/>
                            </td>
                        </tr>
                    </React.Fragment>
                );
            }
        );

        return table;
    };

    return (
        <React.Fragment>
            {
                (isLoading || !isConfigured) && !dataMissing
                    ? (
                        renderLoader("Loading Session Data", 50)
                    )
                    : (
                        dataMissing
                            ? (
                                <div className="w-full h-full justify-center gap-6 items-center inline-flex flex-col">
                                    <div className="w-14 h-14 p-3.5 bg-[#ffe9e2] rounded-full border border-gray-50 justify-center items-center inline-flex">
                                        <img className="w-7 h-7 relative flex-col justify-start items-start flex" src="/icons/data.svg" alt="icon"/>
                                    </div>
                                    <div className="text-wrap w-[80%] break-words">
                                        <div className="self-stretch text-center text-gray-700 text-md font-semibold leading-[18px]">No Session History Available</div>
                                    </div>
                                    <div className="break-words inline-block text-center text-gray-500 text-base font-normal">
                                        {
                                            coachingPlanDataFound
                                                ? (
                                                    <>
                                                        Coaching plan active. No sessions recorded yet.
                                                    </>
                                                )
                                                : (
                                                    <>
                                                    No active coaching plan found.
                                                    </>
                                                )
                                        }
                                    </div>
                                </div>
                            )
                            : (
                                <div className="w-full h-[90%] justify-start items-center inline-flex flex-col">
                                    <div className="w-full h-[10%] gap-5 inline-flex justify-start items-center">
                                        <div className="grow shrink basis-0 text-gray-900 text-xl font-semibold ml-[26px]">
                                            Session History
                                        </div>
                                        <div className=" flex-col justify-start items-start inline-flex mr-[26px]">
                                            {
                                                weekOptions && (
                                                    <CustomDropDown
                                                        defaultValue={selectedWeek}
                                                        options={weekOptions}
                                                        // Callbacks
                                                        onSelectionCallback={setSelectedWeek}
                                                    />
                                                )
                                            }
                                        </div>
                                    </div>
                                    <div className="w-[calc(100%-48px)] grid-col gap-5 justify-start items-center overflow-y-auto mt-5">
                                        {
                                            sessionData[selectedWeek] && Object.keys(sessionData[selectedWeek]).length > 0
                                                ? (
                                                    <table className="w-full h-[100%] divide-gray-700 dark:divide-gray-700 rounded-[10px]">
                                                        <thead className="whitespace-nowrap w-full border-x-2 border-gray-200">
                                                            <tr className="shadow-[inset_0_1px_0_#d3d3d3,inset_0_-1px_0_#d3d3d3] bg-gray-50 border-gray-200 whitespace-nowrap sticky top-0 z-[2]">
                                                                <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px] ">
                                                                    Session Date
                                                                </th>
                                                                <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                                                                    Activities Completed
                                                                    <div className=" align-text-bottom	has-tooltip float-bottom text-center content-center items-center pl-2 object-bottom whitespace-nowrap inline-flex">
                                                                        <span className="tooltip rounded shadow-lg p-1 mt-12 -ml-72">
                                                                            <p>Activities completed in this session in</p>
                                                                            <p>the selected coaching plan week.</p>
                                                                            <p>Sessions may contain more activities</p>
                                                                            <p>completed on different coaching plan weeks.</p>
                                                                        </span>
                                                                        <img className="h-4 w-4 hs-tooltip" src={"/icons/help-circle.svg"} alt="icon"/>
                                                                    </div>
                                                                </th>
                                                                <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                                                                    Motion Time
                                                                </th>
                                                                <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                                                                    Session Badge
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="whitespace-nowrap">
                                                            {sessionData[selectedWeek] && renderTable()}
                                                        </tbody>
                                                    </table>
                                                )
                                                : (
                                                    <div className="w-full justify-center items-center h-full content-center pt-5 text-center inline-flex flex-col align-middle">
                                                        No sessions in current week
                                                    </div>
                                                )
                                        }
                                    </div>
                                </div>
                            )
                    )
            }
        </React.Fragment>
    );
};

export default SessionsPanel;
